import type { Dispatch, SetStateAction } from 'react'

import useCanPurchaseSubscriptionBox from '@context/subscription-box/useCanPurchaseSubscriptionBox'

import ProductTextNodes from '@data/i18n/product.json'

import posthog from 'posthog-js'

import getGtmEcommerceProduct from '@lib/analytics/ecommerce'
import { gtmEvent } from '@lib/analytics/gtm'
import { pushAddToCart } from '@lib/analytics/elevar-data-layer'
import updateItem from '@lib/cart/update-item'
import removeItems from '@lib/cart/remove-items'
import useAddItem from '@lib/cart/use-add-item'
import useCanAddToCart from '@lib/cart/use-can-add-to-cart'
import useCart from '@context/useCartContext'
import { lang } from '@lib/constants'
import type { Breadcrumbs } from '@lib/types'
import type { Product as ProductType, ProductVariant } from '@lib/types/product'
import { isCustomizableBoxVariant } from '@lib/utils/isSubscriptionBox'
import type { CartLineAttribute } from '@lib/cart/add-item'

type AsyncFunction = () => Promise<unknown>

export type AddToCartProps = {
	product: Partial<ProductType>
	variant: Partial<ProductVariant>
	loading: boolean
	setLoading: Dispatch<SetStateAction<boolean>>
	sellingPlanId: string | null
	priceText?: string
	ctaText?: string
	onAddToCart?: () => void
	className?: string
	renderSoldOutButton?: boolean
	icon?: string
	breadcrumbs?: Breadcrumbs | null
	/** Line item attributes (e.g. source); become order line item properties in Shopify */
	lineItemAttributes?: CartLineAttribute[]
}

export default function AddToCart({
	product,
	variant,
	loading,
	setLoading,
	sellingPlanId,
	priceText,
	ctaText = ProductTextNodes.cart.add,
	onAddToCart,
	className = 'button button-primary whitespace-nowrap',
	renderSoldOutButton = true,
	icon,
	breadcrumbs = null,
	lineItemAttributes
}: AddToCartProps) {
	const addItem = useAddItem()
	const { subscriptionBoxInCart, alreadyPurchasedSubscriptionBox } = useCanPurchaseSubscriptionBox()
	const isCustomizableBox = isCustomizableBoxVariant(variant?.id)
	const { items, revalidate } = useCart()

	const canAddProductToCart = useCanAddToCart({ product, variant })

	const productCTAText = loading ? ProductTextNodes.cart.adding : ctaText

	// Add to cart function
	const addToCart = async () => {
		setLoading(true)

		const customizableBoxInCartLineId = items?.find((item) =>
			isCustomizableBoxVariant(item.merchandise.id)
		)?.id

		// Force an item to have a `sellingPlanId` if it requires a selling plan
		if (!sellingPlanId && product.requiresSellingPlan) {
			throw new Error('Product requires a selling plan, but none was selected.')
		}

		// If the product is a subscription box, we need to handle the cart differently
		const originalItemsArray: any[] = items || []

		// Filter the array based on sellingPlanAllocation.
		// Items tagged source=get_started_gift are exempt — they are companion items
		// added by the A/B offer flow and should survive subscription cart cleanup.
		const nonSubscriptionItems: any[] = originalItemsArray.filter(
			(item) =>
				!item.sellingPlanAllocation &&
				!item.merchandise.sku.startsWith('bb-box') &&
				!item.merchandise.sku.startsWith('signature-box') &&
				!item.attributes?.some((a: { key: string; value: string }) => a.key === 'source' && a.value === 'get_started_gift')
		)
		const subscriptionItems: any[] = originalItemsArray.filter(
			(item) =>
				item.sellingPlanAllocation ||
				item.merchandise.sku.startsWith('bb-box') ||
				item.merchandise.sku.startsWith('signature-box')
		)

		async function performRevalidateAndOnAddToCart(
			revalidateFn: AsyncFunction,
			onAddToCartFn?: () => void
		): Promise<void> {
			try {
				await revalidateFn()
				onAddToCartFn?.()
			} catch (error) {
				console.error('Error during revalidate or onAddToCart:', error)
			}
		}

		async function clearCart(): Promise<void> {
			if (isCustomizableBox) {
				if (nonSubscriptionItems.length > 0) {
					await removeItems(
						nonSubscriptionItems
							.filter((item) => item.id !== undefined)
							.map((item) => ({ id: item.id as string }))
					)
				}

				if (customizableBoxInCartLineId) {
					await updateItem({
						id: customizableBoxInCartLineId,
						quantity: 1,
						variantId: variant.id as string,
						sellingPlanId: sellingPlanId || undefined
					})
				} else {
					await addItem({
						variantId: variant.id || '',
						quantity: 1,
						sellingPlanId,
						attributes: lineItemAttributes
					})
				}
			} else if (!isCustomizableBox) {
				if (customizableBoxInCartLineId) {
					await removeItems(subscriptionItems.map((item) => ({ id: item.id || '' })))
				}
				await addItem({
					// @ts-ignore
					variantId: variant?.id,
					quantity: 1,
					sellingPlanId,
					attributes: lineItemAttributes
				})
			}
			await performRevalidateAndOnAddToCart(revalidate, onAddToCart)
			setLoading(false)
		}

		await clearCart()

		// Analytics ***
		const gtmEcommerceProduct = getGtmEcommerceProduct({
			product,
			price: variant?.price?.amount || product?.price?.amount,
			productBreadcrumb: breadcrumbs,
			isSubscriptionBox: !!isCustomizableBox
		})

		// GTM Event
		gtmEvent({
			event: 'analytics_event',
			data: {
				eventData: {
					category: 'ecommerce',
					action: 'addToCart',
					label: product?.name
				},
				ecommerce: {
					currencyCode: variant?.price?.currencyCode || product?.price?.currencyCode,
					add: {
						actionField: { list: breadcrumbs?.[0]?.title || 'membership' },
						products: [gtmEcommerceProduct]
					}
				}
			}
		})

		// Elevar Event
		if (product && product.id && variant && variant.id) {
			pushAddToCart(product as ProductType, 1, breadcrumbs?.[0]?.title || 'membership')
		}

		// PostHog: add_to_cart (for experiments / funnel metrics)
		const priceAmount = variant?.price?.amount ?? product?.price?.amount
		const sourceAttr = lineItemAttributes?.find((a) => a.key === 'source')?.value
		posthog.capture('add_to_cart', {
			product_id: product?.id,
			product_name: product?.name,
			variant_id: variant?.id,
			quantity: 1,
			value: priceAmount != null ? parseFloat(String(priceAmount)) : undefined,
			currency: variant?.price?.currencyCode || product?.price?.currencyCode,
			is_subscription_box: !!isCustomizableBox,
			list: breadcrumbs?.[0]?.title || 'membership',
			...(sourceAttr && { source: sourceAttr })
		})
		// Analytics ***
	}

	if (variant?.price && variant.price?.amount === '0') {
		return null
	}

	// TODO: check if in cart?
	// Show the regular button if the product is available for sale
	if (canAddProductToCart) {
		return (
			<button className={className} type="button" disabled={loading} onClick={addToCart}>
				{icon && <i className={icon} />}
				{productCTAText} {priceText && <span className="md:hidden">({priceText})</span>}
			</button>
		)
	}

	/*
	  Disabled buttons
	*/

	// The product cannot be added to cart
	const disabledButtonStyle = 'button button-primary'

	// If the product requires a selling plan, and no selling plan is selected
	if (product?.requiresSellingPlan && !sellingPlanId) {
		return (
			<>
				<button className={disabledButtonStyle} type="button" disabled>
					{ProductTextNodes.cart.add}
				</button>
				<p className="mt-2 text-xs">This product requires a subscription to be selected.</p>
			</>
		)
	}

	// If the product is a subscription box
	if (isCustomizableBox) {
		// If this plan is already purchased
		if (
			alreadyPurchasedSubscriptionBox &&
			alreadyPurchasedSubscriptionBox.productId === product.id &&
			alreadyPurchasedSubscriptionBox.variantId === variant.id
		) {
			return (
				<button type="button" className={disabledButtonStyle} disabled>
					<i className="mr-1 fa fa-check" /> {ProductTextNodes.product.subscribed}
				</button>
			)
		}

		// If its sibling has already been purchased
		if (alreadyPurchasedSubscriptionBox) {
			// Relatively positioned to prevent CLS when this data is loaded — `p` is absolute
			return (
				<div className="relative flex flex-col justify-end pb-2">
					<button className={disabledButtonStyle} type="button" disabled>
						{lang === 'es-es' ? 'Suscríbete' : 'Subscribe'}
					</button>

					<p
						className="absolute inset-x-0 mt-1 text-xs text-center"
						style={{ transform: 'translate(0, calc(100% + 4px))' }}
					>
						{ProductTextNodes.product.subscribedOtherPlan}
					</p>
				</div>
			)
		}

		// If it's already in the cart
		if (subscriptionBoxInCart && subscriptionBoxInCart.productId === product.id) {
			return (
				<button className={disabledButtonStyle} type="button" disabled>
					<i className="mr-2 fa fa-check" />
					{lang === 'es-es' ? 'Adicional' : 'Added'}
				</button>
			)
		}
	}

	// If the product is not a regular product, and it's not available for sale, it is sold out
	return renderSoldOutButton ? (
		<div className="button button-primary disabled" title="This product is currently out of stock.">
			{ProductTextNodes.cart.outOfStock}
		</div>
	) : null
}
